import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line
import { getCompanyDashboardPath } from '~services/utils';
import {
  fetchReport,
  fetchReview,
  setEditing,
  setReportEditable,
  saveReport,
  saveReview,
  savePiace,
  onLogin,
  fetchPortfolio,
  fetchCompanyRegions,
  fetchKeywords,
  fetchRPQuickCheck,
} from '~actions'; // eslint-disable-line
import {
  getActiveCompany,
  isSuperUser,
} from '~selectors'; // eslint-disable-line

// Components
import ReportFooter from './ReportFooter';
import ReportSummary from './ReportSummary';
import ReportDetails from './ReportDetails';
import ReportAdditionalInfo from './ReportAdditionalInfo';
import ReportQualifications from './ReportQualifications';
import ReportContacts from './ReportContacts';
import ReportPaidHeader from './ReportPaidHeader';
import PortfolioBadge from '../paid/PortfolioBadge';
import LoadingIndicator from '../../components/LoadingIndicator';
import NotFound, { notFoundTypes } from '../common/NotFound';
import Tabber from '../../components/Tabber';
import QuickCheckContainer from './quickcheck.container';

import messages from './messages/Report_messages';
import quickCheckMessages from './messages/QuickCheck_messages';

const propTypes = {
  match: PropTypes.object,
  report: PropTypes.object.isRequired,
  fetchReport: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  fetchReview: PropTypes.func.isRequired,
  fetchPortfolio: PropTypes.func.isRequired,
  portfolio: PropTypes.object.isRequired,
  fetchKeywords: PropTypes.func.isRequired,
  fetchCompanyRegions: PropTypes.func.isRequired,
  locale: PropTypes.object,
  reportError: PropTypes.bool,
  superUser: PropTypes.bool,
  location: PropTypes.object.isRequired
};

class Report extends PureComponent {
  constructor(props) {
    super(props);

    this.tabber = React.createRef();

    this.KNOWN_SECTIONS = ['zeckit', 'quickcheck'];

    let [activeSection] = this.KNOWN_SECTIONS;
    const search = queryString.parse(this.props.location.search);
    if (this.KNOWN_SECTIONS.indexOf(search.section) !== -1) {
      activeSection = search.section;
    }

    this.state = {
      activeSection,
    };
  }

  componentWillMount() {
    const { match, report, session } = this.props;
    const { country, identifier } = match.params;

    if (report.identifier !== identifier) {
      this.props.fetchReport({ country, identifier });
    }

    if (session.isLoggedIn) {
      this.props.fetchReview({
        country,
        identifier,
        user_id: session.client.id,
      });
    }

    // TODO: should we try to check if portfolio is already fetched?
    this.props.fetchPortfolio({ country, identifier });
    this.props.fetchKeywords({ country, identifier });
    this.props.fetchCompanyRegions({ country, identifier });
  }

  generateOgTags() {
    const {
      locale: {
        lang: currentLocale
      },
      report: {
        names,
        about,
        logo_id: logoId,
        identifier }} = this.props;
    const { origin, pathname } = window.location;
    const locales = {
      'fi': 'fi_FI',
      'sv': 'sv_SE',
      'en': 'en_US',
    };

    const tags = [
      {
        property: 'og:url',
        content: `${origin}${pathname}`,
      },
      {
        property: 'og:type',
        content: 'business.business'
      },
      {
        property: 'og:title',
        content: `Zeckit - ${names.display || names.primary}`,
      },
      {
        property: 'og:description',
        content: about || `Y-tunnus: ${identifier}`,
      },
      {
        property: 'og:locale',
        content: locales[currentLocale],
      },
    ];

    // If company has logo, insert it as primary image
    if (logoId) {
      tags.push({
        property: 'og:image',
        content: `${origin}/api/images/${logoId}`,
      });
    }
    tags.push({
      property: 'og:image',
      content: `https://zeckit.com/selvitys/widget/badge/FI/${identifier}.png`,
    });

    Object.keys(locales).forEach((locale) => {
      if (locale !== currentLocale) {
        tags.push({
          property: 'og:locale:alternate',
          content: locales[locale],
        });
      }
    });

    return (
      tags.map(tag =>
        <meta
          key={tag.property}
          property={tag.property}
          content={tag.content}
        />
      )
    );
  }

  get activeSection() {
    const { report } = this.props;
    let activeSection = this.state.activeSection;
    if (activeSection === 'quickcheck'
      && (!report.is_luotettava_kumppani
          || report.is_luotettava_kumppani_blacklisted)) {
      activeSection = 'zeckit';
    }
    return activeSection;
  }

  changeActiveSection = activeSection => {
    if (this.KNOWN_SECTIONS.indexOf(activeSection) !== -1) {
      this.tabber.current.changeTab(activeSection);
    }
  }

  render() {
    const {
      report,
      portfolio,
      locale,
      reportError,
      superUser,
      match
    } = this.props;

    if (reportError.error) {
      return <NotFound type={notFoundTypes.company} locale={locale} />;
    }

    if (!report.identifier || !portfolio.company
      || match.params.country !== report.country
      || match.params.identifier !== report.identifier) {
      return (
        <div className="ReportContainer">
          <div className="main pure-g">
            <div
              className="pure-u-1 no-print"
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <LoadingIndicator iconSize='large' />
            </div>
          </div>
        </div>
      );
    }

    const showPortfolio =
      report.portfolio_enabled &&
      portfolio &&
      portfolio.slides &&
      !!portfolio.slides.length;

    return (
      <div className="ReportContainer">
        <Helmet>
          {this.generateOgTags()}
        </Helmet>
        <div className="main pure-g">
          <div className="pure-u-1-24 hidden-mobile no-print" />
          <div className="pure-u-1-1 pure-u-lg-22-24 pure-u-1-1-print">
            {superUser && (
              <Link
                to={getCompanyDashboardPath(report.country, report.identifier)}
              >
                <FormattedMessage
                  id='report.container.dashboard.link.label'
                  defaultMessage="Go to this company's dashboard"
                />
              </Link>
            )}

            <div style={{ height: 20 }} />

            {!report.identifier ? (
              <div style={{ height: 600, paddingTop: 200 }}>
                <LoadingIndicator color="#333" type="line" params=" la-2x" />
              </div>
            ) : (
              <Tabber
                hideTabs={!report.is_luotettava_kumppani}
                ref={this.tabber}
                tabs={[
                  {
                    id: 'statement-tab-zeckit-button',
                    isActive: this.activeSection === 'zeckit',
                    content: <FormattedMessage { ...messages.title } />,
                  },
                  {
                    id: 'statement-tab-quickcheck-button',
                    isActive: this.activeSection === 'quickcheck',
                    content: (
                      <FormattedMessage { ...quickCheckMessages.title } />
                    ),
                  },
                ]}
              >
                {({ activeTabId }) => {
                  if (activeTabId === 'statement-tab-zeckit-button') {
                    return (
                      <React.Fragment>
                        {report.portfolio_enabled && (
                          <React.Fragment>
                            <ReportPaidHeader
                              {...this.props}
                              showPortfolio={showPortfolio}
                            />
                            <div style={{ height: 20 }} />
                          </React.Fragment>
                        )}

                        <ReportSummary
                          {...this.props}
                          showPortfolio={showPortfolio}
                        />

                        <div style={{ height: 20 }} />

                        {(showPortfolio && !report.portfolio_hidden) && (
                          <PortfolioBadge
                            identifier={report && report.identifier}
                            portfolio={portfolio}
                          />
                        )}

                        {(showPortfolio && !report.portfolio_hidden) && (
                          <div style={{ height: 20 }} />
                        )}

                        <ReportDetails {...this.props} />

                        <div style={{ height: 20 }} />

                        <ReportQualifications {...this.props} />

                        <div style={{ height: 20 }} />

                        <ReportAdditionalInfo
                          {...this.props}
                          showQuickCheck={
                            evnt => {
                              evnt.preventDefault();
                              this.changeActiveSection('quickcheck');
                            }
                          }
                        />

                        <div style={{ height: 20 }} />

                        <ReportContacts {...this.props} />

                        <div style={{ height: 20 }} />
                      </React.Fragment>
                    );
                  }
                  if (activeTabId === 'statement-tab-quickcheck-button') {
                    return <QuickCheckContainer />
                  }
                  return null;
                }}
              </Tabber>
            )}
            <ReportFooter />
          </div>
        </div>
      </div>
    );
  }
}

Report.propTypes = propTypes;

// This makes state objects available to the component via props!
function mapStateToProps(state) {
  return {
    report: state.report,
    reportEditable: state.reportEditable,
    session: state.session,
    review: state.review,
    locale: state.locale,
    hasPiaced: !!state.report.hasPiaced,
    portfolio: state.portfolio.portfolio,
    keywords: state.paidFeatures.keywords,
    companyLocalities: state.paidFeatures.localities,
    reportError: state.errors.report,
    superUser: isSuperUser(state),
    company: getActiveCompany(state),
  };
}

// This adds action creators to components props
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchReport,
      setEditing,
      setReportEditable,
      saveReport,
      saveReview,
      fetchReview,
      savePiace,
      onLogin,
      fetchPortfolio,
      fetchKeywords,
      fetchCompanyRegions,
      fetchRPQuickCheck,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Report);
